/**
 * Racking Inspection Screen
 * Main screen for racking inspection functionality
 * This screen provides interfaces for walk observations and damage checklists
 *
 * @module RackingInspection
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
  RefreshControl,
} from "react-native";
import { useTheme } from "../../theme";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import TabbedNavigation from "../../components/TabbedNavigation";
import { RACKING_INSPECTION_TABS } from "./constants";
import RackingInspectionBeginPage from "./RackingInspectionBeginPage";
import { getYearOptions } from "../../utils/filterOptions";
import { useToast } from "../../components/LhToastContainer";

// Import types
import DamageChecklistTab from "./components/DamageChecklistTabWithRedux";
import RackingInspectionWelcome from "./components/RackingInspectionWelcome";
import WalkObservationsTab from "./components/WalkObservationsTabWithRedux";
import MobileFilterOverlay from "../../components/MobileFilterOverlay";
import { fetchRackingInspectionByIdData } from "../../components/racking_inspection/fetchRackingInspectionByIdData";
import { fetchWalkObservationsData } from "../../components/racking_inspection/fetchWalkObservationsData";
import { fetchDamageChecklistData } from "../../components/racking_inspection/fetchDamageChecklistData";
import { getWalkObservationFilterOptions } from "./getWalkObservationFilterOptions";
import { getDamageChecklistFilterOptions } from "./getDamageChecklistFilterOptions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  RackingInspectionData,
  RackingInspectionScreenProps,
  TabId,
} from "./types";
import { clearSelectedRackingWalk } from "../../store/slices/selectedRackingWalkSlice";
import {
  clearSuccessMessage,
  setSuccessMessage,
  clearQuestions,
} from "../../store/slices/rackingInspectionQuestionsSlice";

/**
 * Main Racking Inspection screen component
 *
 * @param props Component properties
 * @returns RackingInspection screen component
 */
const RackingInspectionScreen: React.FC<RackingInspectionScreenProps> = ({ onHomePress }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // State management
  //row data can be different based on the tab
  const [selectedRowData, setSelectedRowData] =
    useState<RackingInspectionData | null>(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("walk-observations");
  const [, setShowActiveItemsOnly] = useState(true);
  // Filter overlay states
  const [, setFilterOverlayVisible] = useState(false);
  const [, setShowValidation] = useState(false);
  const [filters, setFilters] = useState({
    periodWeek: "",
    year: "",
  });
  const [refreshing, setRefreshing] = useState(false);
  
  // Refs to child components for calling their internal methods
  const walkObservationsTabRef = useRef<any>(null);
  const damageChecklistTabRef = useRef<any>(null);
  // Navigation state for Begin page
  const [showBeginPage, setShowBeginPage] = useState(false);
  // Mobile filter overlay state
  const [showMobileFilterOverlay, setShowMobileFilterOverlay] = useState(false);
  // Mobile filter trigger for prop-based filtering
  const [mobileFilterTrigger, setMobileFilterTrigger] = useState<{
    filters: Record<string, any>;
    timestamp: number;
  } | null>(null);
  // For walk observation begin page navigation
  const [beginPageUid, setBeginPageUid] = useState<string | null>(null);
  const [beginPageWalkStartedBy, setBeginPageWalkStartedBy] =
    useState<string>("");
  const [, setBeginPageLoading] = useState(false);
  const [, setBeginPageError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const toast = useToast();
  // Get selected racking walk data from Redux
  const selectedRackingWalk = useSelector(
    (state: any) => state.selectedRackingWalk
  );
  // Get success message from redux
  const successMessage = useSelector(
    (state: RootState) => state.rackingInspectionQuestions?.successMessage
  );
  // Get the current period week from Redux
  const periodWeekData = useSelector(
    (state: any) => state.appData.periodWeek?.data
  );
  // Get filter options from Redux store for MobileFilterOverlay
  const filterOptions = useSelector(
    (state: RootState) => state.rackingWalkObservations.filterOptions
  );
  const employees = useSelector((state: RootState) => state.appData.employees);
  const walkObservationFilters = useSelector(
    (state: RootState) => state.rackingWalkObservations.filters
  );
  // Get selected location from Redux store at the component level
  const selectedLocation = useSelector(
    (state: any) => state.appData.selectedLocation
  );
  const locationName = selectedLocation?.name || selectedLocation?.displayText;
  // Get walk observations data and loading state for checking in-progress inspections
  const walkObservations = useSelector(
    (state: RootState) => state.rackingWalkObservations.walkObservations
  );
  const walkObservationsLoading = useSelector(
    (state: RootState) => state.rackingWalkObservations.loading
  );
  // Get damage checklist filter options and filters from Redux store
  const damageChecklistFilterOptions = useSelector(
    (state: RootState) => state.rackingDamageChecklist.filterOptions
  );
  const damageChecklistFilters = useSelector(
    (state: RootState) => state.rackingDamageChecklist.filters
  );

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Load walk observations data when component mounts or location changes
  useEffect(() => {
    if (locationName) {
      fetchWalkObservationsData({
        setIsLoading: () => {},
        setApiError: () => {},
        dispatch,
        location: locationName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName]);

  /**
   * Handler for opening the Begin page from WalkObservationsTab
   * @param uid Walk observation UID
   * @param walkStartedBy The user who started the walk
   * @param status Status of the walk
   */
  const handleBeginPageOpen = async (
    uid: string,
    walkStartedBy: string,
    status?: number
  ) => {
    setBeginPageUid(uid);
    setBeginPageWalkStartedBy(walkStartedBy);
    setBeginPageError(null);

    // Store status for passing to Begin page
    const statusFromRow = status !== undefined ? status : null;
    console.log("handleBeginPageOpen - received status:", statusFromRow);

    // If already loaded, just show the page
    if (
      selectedRackingWalk.data &&
      selectedRackingWalk.data.PW &&
      selectedRackingWalk.data.PW === uid
    ) {
      // If we have a status from the row, make sure it's included in the data
      if (
        statusFromRow !== null &&
        selectedRackingWalk.data.Status === undefined
      ) {
        // Update the data in the Redux store to include the status
        dispatch({
          type: "selectedRackingWalk/setData",
          payload: { ...selectedRackingWalk.data, Status: statusFromRow },
        });
      }
      setShowBeginPage(true);
      return;
    }

    setBeginPageLoading(true);
    try {
      await fetchRackingInspectionByIdData({
        dispatch,
        uid,
        walkStartedBy,
        setIsLoading: setBeginPageLoading,
        setApiError: setBeginPageError,
      });

      // If we have a status from the row and API response doesn't include it,
      // update the data in the Redux store
      if (
        statusFromRow !== null &&
        selectedRackingWalk.data &&
        selectedRackingWalk.data.Status === undefined
      ) {
        dispatch({
          type: "selectedRackingWalk/setData",
          payload: { ...selectedRackingWalk.data, Status: statusFromRow },
        });
      }

      setShowBeginPage(true);
    } catch (err: any) {
      setBeginPageError(err?.message || "Failed to load inspection details");
    } finally {
      setBeginPageLoading(false);
    }
  };

  // Helper for responsive UI
  const isMobile = Dimensions.get("window").width < 768;

  // Mobile filter overlay handlers
  const handleMobileFilterPress = () => {
    // Reset the mobile filter overlay selections before showing it
    if (activeTab === "walk-observations" && walkObservationsTabRef.current) {
      if (walkObservationsTabRef.current.resetMobileFilterOverlay) {
        walkObservationsTabRef.current.resetMobileFilterOverlay();
      }
    }
    setShowMobileFilterOverlay(true);
  };

  const handleMobileFilterCancel = () => {
    setShowMobileFilterOverlay(false);
  };

  const handleMobileFilterProceed = async (
    nextFilters: Partial<Record<string, any>> = {}
  ) => {
    console.log("📱 PARENT: Mobile Filter Proceed clicked for tab:", activeTab);
    console.log("📱 PARENT: nextFilters received from overlay:", nextFilters);
    
    // Use prop-based approach to trigger filtering in child components
    setMobileFilterTrigger({
      filters: nextFilters,
      timestamp: Date.now()
    });
    
    // Close the overlay
    setShowMobileFilterOverlay(false);
  };







  /**
   * Handle filter changes from filter components
   * @param key Filter key
   * @param value Selected filter value
   */
  const handleFilterChange = (key: string, value: any) => {
    console.log(`📱 PARENT: Filter Changed: ${key}`, value);
    
    // Route filter changes to the appropriate child component based on active tab
    if (activeTab === "damage-checklist" && damageChecklistTabRef.current) {
      console.log("📱 PARENT: Routing filter change to DamageChecklistTab");
      // Call the child component's filter change handler
      if (damageChecklistTabRef.current.handleFilterChange) {
        damageChecklistTabRef.current.handleFilterChange(key, value);
      }
    } else if (activeTab === "walk-observations" && walkObservationsTabRef.current) {
      console.log("📱 PARENT: Routing filter change to WalkObservationsTab");
      // Call the child component's filter change handler
      if (walkObservationsTabRef.current.handleFilterChange) {
        walkObservationsTabRef.current.handleFilterChange(key, value);
      }
    }
  };

  /**
   * Handle row click in data table
   * @param rowData Data of the clicked row
   */
  const handleRowClick = (rowData: RackingInspectionData) => {
    console.log("Row clicked:", rowData);
    setSelectedRowData(rowData);
    setIsOverlayVisible(true);
  };

  /**
   * Close detail overlay
   */
  const closeOverlay = () => {
    setIsOverlayVisible(false);
    setSelectedRowData(null);
  };

  /**
   * Toggle active items filter
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleActiveItems = () => {
    setShowActiveItemsOnly((prev) => !prev);
  };

  /**
   * Open begin page directly with current period week from Redux
   */
  const openBeginPage = () => {
    if (periodWeekData && periodWeekData.period && periodWeekData.week) {
      // Check if there's any in-progress inspection for the current period
      const hasInProgressInCurrentPeriod = walkObservations.some(
        (observation: any) => {
          const periodWeek = observation.PW || observation.RSPW || "";
          const status =
            observation.Status !== undefined
              ? observation.Status
              : observation.WalkStatus;

          // Extract period from PW (e.g., "P7W4" -> "P7")
          const observationPeriod = periodWeek.split("W")[0];
          const currentPeriodStr = `P${periodWeekData.period}`;

          // Check if it's the current period and status is 0 (In Progress)
          return (
            observationPeriod === currentPeriodStr &&
            (status === 0 || status === "0")
          );
        }
      );

      if (hasInProgressInCurrentPeriod) {
        // Show toast notification and don't allow new inspection
        toast.showToast({
          header: "Inspection In Progress",
          subtext: `Already inspection for the current period P${periodWeekData.period} is In Progress`,
          variant: "warning",
          position: "top",
        });
        return;
      }

      // Format as string P1W2 format
      const formattedPeriodWeek = `P${periodWeekData.period}W${periodWeekData.week}`;

      // Set the period week in filters state
      setFilters((prev) => ({
        ...prev,
        periodWeek: formattedPeriodWeek,
      }));

      // Show the begin page (no UID required)
      setShowBeginPage(true);
    } else {
      // Fallback to the original behavior if period week data isn't available
      setFilterOverlayVisible(true);
    }
  };

  /**
   * Close filter overlay
   */
  const closeFilterOverlay = () => {
    setFilterOverlayVisible(false);
  };

  /**
   * Refresh walk observations data from API
   * Using the locationName from component scope
   */
  const refreshWalkObservationsData = async () => {
    if (locationName) {
      console.log(
        "Refreshing walk observations data for location:",
        locationName
      );
      // Use the already imported fetchWalkObservationsData function
      await fetchWalkObservationsData({
        setIsLoading: () => {},
        setApiError: () => {},
        dispatch,
        location: locationName,
      });
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([refreshWalkObservationsData(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  }, [refreshWalkObservationsData]);

  /**
   * Handle going back from the Begin page to the main screen
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBeginPageBack = () => {
    setShowBeginPage(false);
    // Clear questions from Redux to ensure fresh data on next inspection
    dispatch(clearQuestions());
    // Refresh walk observations data to show latest updates
    refreshWalkObservationsData();
  };

  /**
   * Handle saving inspection data from Begin page
   */
  const handleSaveInspection = (status: "in-progress" | "completed") => {
    console.log("Inspection saved with status:", status);

    // Set success message in Redux
    const successMessage = `Racking Inspection ${
      status === "completed" ? "completed" : "saved"
    } successfully`;
    dispatch(setSuccessMessage(successMessage));

    setShowBeginPage(false);
    dispatch(clearSelectedRackingWalk());
    // Clear questions from Redux to ensure fresh data on next inspection
    dispatch(clearQuestions());
    // Refresh walk observations data to show the newly saved inspection
    refreshWalkObservationsData();
  };

  /**
   * Handle saving filters and proceeding
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveFilters = () => {
    // Check if any required fields are empty
    const requiredFieldsEmpty = Object.values(filters).some(
      (value) => value === ""
    );

    if (requiredFieldsEmpty) {
      // Show validation errors and don't proceed
      setShowValidation(true);
    } else {
      // All required fields are filled, proceed
      setShowValidation(false);
      closeFilterOverlay();

      // Show the begin page (no UID required)
      console.log("Proceeding with filters:", filters);
      setShowBeginPage(true);
    }
  }; // Get user email from Redux state
  const userEmail = useSelector((state: any) => state.auth?.user?.username);

  // Memoize walkData so its reference only changes when the underlying data
  // actually changes, preventing the merge useEffect in RackingInspectionBeginPage
  // from firing on every parent re-render and wiping locally uploaded image URLs.
  const memoizedWalkData = useMemo(() => {
    const raw = selectedRackingWalk.data;
    if (!beginPageUid || !raw) return raw;
    if (raw.Status === undefined && raw.rawData?.Status !== undefined) {
      return { ...raw, uid: beginPageUid, walkStartedBy: beginPageWalkStartedBy, Status: raw.rawData.Status };
    }
    return { ...raw, uid: beginPageUid, walkStartedBy: beginPageWalkStartedBy };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRackingWalk.data, beginPageUid, beginPageWalkStartedBy]);

  // Show begin page if selected

  // Show Begin page for Walk Observations (no UID required)
  if (showBeginPage) {
    console.log(
      "RackingInspectionScreen - selectedRackingWalk.data:",
      selectedRackingWalk.data
    );
    return (
      <RackingInspectionBeginPage
        onBackPress={() => {
          setShowBeginPage(false);
          setBeginPageUid(null);
          setBeginPageWalkStartedBy("");
          // Clear questions from Redux to ensure fresh data on next inspection
          dispatch(clearQuestions());
          // Refresh walk observations data when returning via header back button
          refreshWalkObservationsData();
        }}
        onHomePress={onHomePress}
        onSave={handleSaveInspection}
        periodWeek={memoizedWalkData?.PW || filters.periodWeek}
        userEmail={userEmail}
        walkData={beginPageUid ? memoizedWalkData : undefined}
      />
    );
  }

  // Show MobileFilterOverlay when requested
  if (showMobileFilterOverlay) {
    // Get the appropriate filter options and current filters based on active tab
    const getFilterOptionsForActiveTab = () => {
      if (activeTab === "damage-checklist") {
        // Use damage checklist filter options from Redux store (already selected at top level)
        const filterOptions = getDamageChecklistFilterOptions(
          damageChecklistFilterOptions,
          employees,
          damageChecklistFilters
        );
        console.log("📱 PARENT: Opening MobileFilterOverlay for damage-checklist");
        console.log("📱 PARENT: Current Redux filters:", damageChecklistFilters);
        console.log("📱 PARENT: Filter options being passed:", filterOptions);
        
        return {
          filterOptions,
          currentFilters: damageChecklistFilters,
        };
      } else {
        // Default to walk observations
        return {
          filterOptions: getWalkObservationFilterOptions(
            filterOptions,
            employees,
            walkObservationFilters
          ),
          currentFilters: walkObservationFilters,
        };
      }
    };

    const {
      filterOptions: tabFilterOptions,
      currentFilters: tabCurrentFilters,
    } = getFilterOptionsForActiveTab();

    return (
      <MobileFilterOverlay
        visible={showMobileFilterOverlay}
        onDismiss={handleMobileFilterCancel}
        filters={tabFilterOptions}
        onFilterChange={handleFilterChange}
        onProceed={handleMobileFilterProceed}
        onCancel={handleMobileFilterCancel}
        currentFilters={tabCurrentFilters}
      />
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1 }}
    >
      <ScrollView
        style={[{ flex: 1 }, isDark && { backgroundColor: colors.background.page }]}
        contentContainerStyle={[styles.container, isDark && { backgroundColor: colors.background.page }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={isDark ? colors.brand.primary : "#0066CC"}
            colors={[isDark ? colors.brand.primary : "#0066CC"]}
            title="Refreshing..."
          />
        }
      >
        {/* Success Message Banner */}
        {successMessage && (
          <View style={styles.successMessageContainer}>
            <MaterialIcons
              name="check-circle"
              size={24}
              color="#4CAF50"
              style={styles.successIcon}
            />
            <Text style={styles.successMessageText}>{successMessage}</Text>
          </View>
        )}

        <RackingInspectionWelcome
          openFilterOverlay={openBeginPage}
          isLoading={walkObservationsLoading}
        />
        {/* Tabs and Tab Content Container */}
        <View style={[styles.tabsAndContentContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
          {/* Tabs */}
          <View style={styles.tabsContainer}>
            <TabbedNavigation
              tabs={RACKING_INSPECTION_TABS}
              initialActiveTab={"walk-observations"}
              onTabChange={(tabId) => setActiveTab(tabId as TabId)}
            />
          </View>
          {/* Tab Content */}
          <View style={styles.tabContentContainer}>
            {activeTab === "walk-observations" && (
              <WalkObservationsTab
                ref={walkObservationsTabRef}
                isOverlayVisible={isOverlayVisible}
                selectedRowData={selectedRowData}
                closeOverlay={closeOverlay}
                onRowClick={handleRowClick}
                onFilterChange={handleFilterChange}
                onBeginPageOpen={handleBeginPageOpen}
                showMobileFilterButton={isMobile}
                onMobileFilterPress={handleMobileFilterPress}
                onMobileFilterProceed={handleMobileFilterProceed}
                mobileFilterTrigger={activeTab === "walk-observations" ? (mobileFilterTrigger || undefined) : undefined}
              />
            )}
            {activeTab === "damage-checklist" && (
              <DamageChecklistTab
                ref={damageChecklistTabRef}
                isOverlayVisible={isOverlayVisible}
                selectedRowData={selectedRowData}
                closeOverlay={closeOverlay}
                onFilterChange={handleFilterChange}
                showMobileFilterButton={isMobile}
                onMobileFilterPress={handleMobileFilterPress}
                onMobileFilterProceed={handleMobileFilterProceed}
                mobileFilterTrigger={activeTab === "damage-checklist" ? (mobileFilterTrigger || undefined) : undefined}
              />
            )}
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
     paddingVertical: "1.5%",
    paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
    backgroundColor: "#F5F9FF",
    minHeight: "100%",
  },
  welcomeCard: {
    backgroundColor: "white",
    borderRadius: 8,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#0066CC",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    justifyContent: "space-between",
    flexWrap: Platform.OS === "web" ? "nowrap" : "wrap",
  },
  welcomeText: {
    fontSize: Platform.select({ web: 24, default: 20 }),
    fontWeight: "500",
    color: "#212529",
    marginLeft: 12,
  },
  formSection: {},
  sectionHeader: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
  },
  sectionHeader1: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
    marginLeft: 30,
  },
  sectiontableHeader: {
    backgroundColor: "#004B93",
    padding: 16,
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 5,
  },
  textHeader: {
    color: "#fff",
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    textAlign: "center",
  },
  dropdownContainer: {
    marginBottom: 10,
  },
  addButton: {
    padding: 12,
    borderRadius: 20,
    alignItems: "center",
    marginLeft: 50,
    width: Platform.OS === "web" ? "15%" : "50%",
  },
  mobileAddButton: {
    alignSelf: "center",
    marginLeft: 0,
    marginRight: 0,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: Platform.select({ web: 16, default: 14 }),
  },
  divider: {
    height: 1,
    backgroundColor: "#ddd",
    marginVertical: 20,
  },
  filterContainer: {
    marginTop: 10,
  },
  tabsAndContentContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d7e5f8",
    marginBottom: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  tabContentContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  tabsContainer: {
    backgroundColor: "transparent",
    borderRadius: 0,
    overflow: "hidden",
    borderWidth: 0,
    paddingTop: 22,
    paddingLeft: 22,
  },
  rowFlex: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  returnButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  returnButtonIcon: {
    color: "white",
    fontSize: 16,
    marginRight: 4,
  },
  returnButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "500",
  },
  cardContainer: {
    padding: 16,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  cardGridItem: {
    width: "48%", // Two cards per row with some spacing
    marginBottom: 16,
  },
  completedButton: {
    minWidth: Platform.OS === "web" ? 120 : undefined, // ensure enough width on web
    maxWidth: Platform.OS === "web" ? 160 : undefined,
    flexGrow: 1,
    alignSelf: "center",
    paddingHorizontal: 12,
    backgroundColor: "green",
  },
  completedButtonText: {
    fontSize: 14,
    flexShrink: 1,
    textAlign: "center",
  },
  table: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#000",
  },
  headerRow: {
    flexDirection: "row",
  },
  fullHeaderCell: {
    flex: 1,
    textAlign: "center",
    padding: 10,
    backgroundColor: "#77ab59",
    color: "#fff",
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  cellTitle: {
    width: "30%",
    padding: 8,
    borderRightWidth: 1,
    borderColor: "#000",
    fontWeight: "bold",
  },
  cellDesc: {
    width: "70%",
    padding: 8,
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E8F5E9",
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginHorizontal: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#4CAF50",
  },
  successIcon: {
    marginRight: 12,
  },
  successMessageText: {
    flex: 1,
    fontSize: 16,
    color: "#2E7D32",
  },
});

export default RackingInspectionScreen;
